<template>

    <div v-if="!requisicoes.finalizado" class="loading-overlay">
        <div class="spinner"></div>
    </div>


    <div v-if="requisicoes.finalizado">
        <!-- Info Banner -->
        <div class="form-info-banner">
            <h3 class="form-info-banner-title">Os campos com * são de preenchimento obrigatório</h3>
            <p class="form-info-banner-text">Caso queira, você poderá salvar e continuar depois.</p>
        </div>

        <!-- Seção: Documentos -->
        <section class="form-section">
            <div class="form-section-header">
                <LabelDisclaimer :TamanhoFonteLabel="'H5'" :NomeLabel="'Documentos'"
                    :TipoCliente="'Pessoa Jurídica'" :Sessao="'Documentos'" :SubSessao="'Documentos'" />
                <p class="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    Todos os arquivos marcados como obrigatórios deverão ser anexados
                </p>
            </div>
            <div class="form-section-content">
                <div class="form-subsection">
                    <h4 class="form-subsection-title">
                        <i class="fa fa-upload"></i>
                        Upload de Arquivos
                    </h4>
                    <div class="form-grid-1">
                        <div class="form-field">
                            <label class="form-field-label form-field-label-required" for="tipoCadastro">Tipo de documento</label>
                            <div class="flex gap-4 items-end">
                                <div class="flex-1">
                                    <select id="tipoCadastro" class="field" v-model="TipoDocumento">
                                        <option value="">Selecione</option>
                                        <option v-for="item in listaDocumentosFiltrados" :key="item.Id" :value="item.Id">
                                            {{ item.Nome }}{{ item.Obrigatorio == 'Sim' ? ' - Obrigatório' : '' }}
                                        </option>
                                    </select>
                                </div>
                                <div v-for="(documento, index) in listaDocumentosFiltradosComTipo" :key="index">
                                    <!-- Atributos específicos para melhorar compatibilidade com Safari iOS -->
                                    <input type="file" 
                                        :id="'fileInput' + index" 
                                        :ref="'fileInput' + index"
                                        accept="image/*,application/pdf"
                                        style="display: none" 
                                        @change="handleFileChange($event, documento.Id)">
                                    
                                    <!-- Botão para acionar o input file -->
                                    <button class="btn-primary" 
                                            @click.prevent="triggerFileInput(index)">
                                        <i class="fa fa-upload mr-2"></i>
                                        Anexar arquivo
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Seção: Arquivos Pendentes de Envio -->
        <section class="form-section" v-if="arquivosUpload.length > 0">
            <div class="form-section-header">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Arquivos Pendentes de Envio
                    <span class="ml-2 text-sm font-normal text-blue-600 dark:text-blue-400">
                        ({{ arquivosUpload.length }} arquivo{{ arquivosUpload.length > 1 ? 's' : '' }})
                    </span>
                </h3>
                <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Estes arquivos serão enviados ao clicar em "Avançar"
                </p>
            </div>
            <div class="form-section-content">
                <div class="space-y-2">
                    <div 
                        v-for="(arquivo, index) in arquivosUpload" 
                        :key="index"
                        class="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
                    >
                        <div class="flex items-center gap-3 flex-1 min-w-0">
                            <i class="fa fa-file-alt text-blue-600 dark:text-blue-400"></i>
                            <div class="flex-1 min-w-0">
                                <p class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                    {{ arquivo.FileName }}
                                </p>
                                <p class="text-xs text-gray-600 dark:text-gray-400">
                                    {{ getNomeDoDocumento(arquivo.TipoDocumentoId) }}
                                </p>
                            </div>
                        </div>
                        <button
                            @click="removerArquivoPendente(index)"
                            class="ml-2 p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                            title="Remover da fila"
                        >
                            <i class="fa fa-times"></i>
                        </button>
                    </div>
                </div>
            </div>
        </section>

        <!-- Seção: Documentos Anexados -->
        <section class="form-section" v-if="arquivos.length > 0">
            <div class="form-section-header">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Documentos Anexados</h3>
            </div>
            <div class="form-section-content">
                <div class="hidden md:block overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead class="bg-gray-50 dark:bg-gray-800">
                            <tr>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tipo de documento</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nome do arquivo</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Data/Hora</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                        <tbody v-if="requisicoes.listaDocumentosUploadEfetuado" class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                            <tr v-for="(arquivo, index) in arquivos" :key="index" class="hover:bg-gray-50 dark:hover:bg-gray-800">
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{{ getNomeDoDocumento(arquivo.TipoDocumentoId) }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{{ arquivo.FileName }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{{ formatDateTime(arquivo.InsertDate) }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm">
                                    <button 
                                        v-if="!Edicao"
                                        @click.stop.prevent="excluirArquivo(arquivo, index)" 
                                        :disabled="deletando == index"
                                        :class="{ 'opacity-50 cursor-not-allowed': deletando == index }"
                                        class="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 focus:outline-none"
                                        title="Deletar arquivo">
                                        <i v-if="deletando != index" class="fa fa-trash"></i>
                                        <i v-if="deletando == index" class="fa fa-spinner fa-spin"></i>
                                    </button>
                                </td>
                            </tr>
                        </tbody>

                        <tbody v-if="!requisicoes.listaDocumentosUploadEfetuado" class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                            <tr v-for="(arquivo, index) in arquivos" :key="index">
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{{ getNomeDoDocumento(arquivo.TipoDocumentoId) }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{{ arquivo.FileName }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{{ formatDateTime(arquivo.InsertDate) }}</td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm"></td>
                            </tr>
                            <tr v-for="i in 1" :key="'loading-'+i">
                                <td colspan="4" class="px-6 py-4"><div class="shimmer h-4 w-full rounded"></div></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                
                <!-- Mobile Cards -->
                <div class="md:hidden space-y-3">
                    <!-- Cards quando documentos carregados -->
                    <template v-if="requisicoes.listaDocumentosUploadEfetuado">
                        <div 
                            v-for="(arquivo, index) in arquivos" 
                            :key="index"
                            class="border border-gray-200 dark:border-slate-700 rounded-lg p-4 space-y-3 bg-white dark:bg-slate-800"
                        >
                            <div>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Tipo de documento</p>
                                <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {{ getNomeDoDocumento(arquivo.TipoDocumentoId) }}
                                </p>
                            </div>

                            <div>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Nome do arquivo</p>
                                <p class="text-sm text-gray-700 dark:text-gray-300">
                                    {{ arquivo.FileName }}
                                </p>
                            </div>

                            <div>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Data/Hora</p>
                                <p class="text-sm text-gray-700 dark:text-gray-300">
                                    {{ formatDateTime(arquivo.InsertDate) }}
                                </p>
                            </div>

                            <div v-if="!Edicao" class="pt-2 border-t border-gray-200 dark:border-slate-700">
                                <button
                                    @click.stop.prevent="excluirArquivo(arquivo, index)"
                                    :disabled="deletando == index"
                                    :class="{ 'opacity-50 cursor-not-allowed': deletando == index }"
                                    class="w-full px-4 py-2 text-sm font-medium text-white bg-red-600 dark:bg-red-700 rounded-lg hover:bg-red-700 dark:hover:bg-red-800 transition-colors flex items-center justify-center gap-2"
                                    title="Deletar arquivo"
                                >
                                    <i v-if="deletando != index" class="fa fa-trash"></i>
                                    <i v-if="deletando == index" class="fa fa-spinner fa-spin"></i>
                                    <span>{{ deletando == index ? 'Deletando...' : 'Deletar arquivo' }}</span>
                                </button>
                            </div>
                        </div>
                    </template>

                    <!-- Loading state -->
                    <template v-if="!requisicoes.listaDocumentosUploadEfetuado">
                        <div 
                            v-for="(arquivo, index) in arquivos" 
                            :key="index"
                            class="border border-gray-200 dark:border-slate-700 rounded-lg p-4 space-y-3 bg-white dark:bg-slate-800"
                        >
                            <div>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Tipo de documento</p>
                                <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {{ getNomeDoDocumento(arquivo.TipoDocumentoId) }}
                                </p>
                            </div>

                            <div>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Nome do arquivo</p>
                                <p class="text-sm text-gray-700 dark:text-gray-300">
                                    {{ arquivo.FileName }}
                                </p>
                            </div>

                            <div>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Data/Hora</p>
                                <p class="text-sm text-gray-700 dark:text-gray-300">
                                    {{ formatDateTime(arquivo.InsertDate) }}
                                </p>
                            </div>
                        </div>
                        
                        <!-- Loading skeleton -->
                        <div class="border border-gray-200 dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-800">
                            <div class="shimmer h-4 w-full rounded"></div>
                        </div>
                    </template>
                </div>
            </div>
        </section>
    </div>
    <ModalAlerta v-if="modalAlerta.mostrar" :mostrar="modalAlerta.mostrar" :tipo="modalAlerta.tipo" :titulo="modalAlerta.titulo" :mensagem="modalAlerta.mensagem" @fechar="modalAlerta.mostrar = false" />
</template>


<script>

import api from '@/api/axios';
import { mapGetters } from 'vuex';
import formatData from '@/utils/formatData';
import LabelDisclaimer from '@/components/LabelDisclaimer.vue'
import ModalAlerta from '../Utilitarios/ModalAlerta.vue';

export default {
    mixins: [formatData],
    components: { LabelDisclaimer, ModalAlerta },
    name: 'CadastroPF_UploadDocumentos',
    props: ['Edicao'],
    computed: {
        ...mapGetters({
            user: 'auth/user',
            listaDocumentos: 'GET_LISTA_DOCUMENTOS_UPLOAD',
            listaDocumentosUploadEfetuado: 'GET_LISTA_DOCUMENTOS_UPLOAD_REALIZADO'
        }),
        listaDocumentosFiltrados() {
            return this.listaDocumentos.filter(
                documento => documento.TipoCliente === 'Pessoa Jurídica' || documento.TipoCliente === 'Todos'
            );
        },
        listaDocumentosFiltradosComTipo() {
            return this.listaDocumentos.filter(
                documento => (documento.TipoCliente === 'Pessoa Jurídica' || documento.TipoCliente === 'Todos') && documento.Id == this.TipoDocumento
            );
        },
    },
    data() {
        return {
            modalAlerta: {
                mostrar: false,
                tipo: 'warning',
                titulo: '',
                mensagem: ''
            },
            permitirAvancar: false,
            TipoDocumento: "",
            deletando: -1,
            requisicoes: {
                listaDocumentos: false,
                listaDocumentosUploadEfetuado: false,
                finalizado: false
            },
            arquivosUpload: [],
            arquivos: [],
            arquivo: {
                TipoDocumentoId: '',
                FileName: '',
                Descricao: '',
                FileDataString64: '',
                UsuarioInsert: '',
                Data: new Date()
            },
        }
    },
    created() {
        this.$store.dispatch('FETCH_CONSULTA_LISTA_DOCUMENTOS_UPLOAD');
        this.$store.dispatch('FETCH_CONSULTA_LISTA_DOCUMENTOS_UPLOAD_REALIZADO');

    },
    mounted() {
        this.executarListagemUploads();
    },
    watch: {
        listaDocumentosUploadEfetuado() {
            this.executarListagemUploads();
        },
        listaDocumentos() {
            this.verificarSeRequisicoesFinalizaram('listaDocumentos')
        }
    },
    methods: {
        executarListagemUploads(){
            this.verificarSeRequisicoesFinalizaram('listaDocumentos');
            this.verificarSeRequisicoesFinalizaram('listaDocumentosUploadEfetuado');
            if (this.listaDocumentosUploadEfetuado != null) {
                // Não limpar arquivosUpload aqui - apenas atualizar lista de arquivos já salvos
                this.arquivos = this.listaDocumentosUploadEfetuado;
            }
            this.validarFormulario();
        },
        verificarSeRequisicoesFinalizaram(requisicao) {
            if (requisicao == 'listaDocumentos') {
                this.requisicoes.listaDocumentos = true;
            } else if (requisicao == 'listaDocumentosUploadEfetuado') {
                this.requisicoes.listaDocumentosUploadEfetuado = true;
            }

            if (this.requisicoes.listaDocumentosUploadEfetuado && this.requisicoes.listaDocumentos) {
                this.requisicoes.finalizado = true;
            }
        },
        getNomeDoDocumento(tipoDocumentoId) {
            const documento = this.listaDocumentos.find(doc => doc.Id === tipoDocumentoId);
            return documento ? documento.Nome : 'Documento não encontrado';
        },
        validarFormulario(submeter) {
            // Guard: se lista de documentos não foi carregada, não pode validar
            if (!this.listaDocumentos || this.listaDocumentos.length === 0) {
                if (submeter) {
                    this.modalAlerta = {
                        mostrar: true,
                        tipo: 'warning',
                        titulo: 'Aguarde',
                        mensagem: 'Carregando lista de documentos...'
                    };
                }
                return false;
            }

            this.permitirAvancar = true;
            var nomeDoCampo = '';

            this.listaDocumentos.forEach(obj => {
                if ((obj.TipoCliente == 'Pessoa Jurídica' || obj.TipoCliente == 'Todos') && obj.Obrigatorio == 'Sim') {
                    // Verificar tanto em arquivos já salvos quanto em arquivos pendentes
                    const temArquivoSalvo = this.arquivos.some(arquivo => arquivo.TipoDocumentoId === obj.Id);
                    const temArquivoPendente = this.arquivosUpload.some(arquivo => arquivo.TipoDocumentoId === obj.Id);
                    
                    if (!temArquivoSalvo && !temArquivoPendente) {
                        if (this.permitirAvancar) {
                            nomeDoCampo = obj.Nome;
                        }
                        this.permitirAvancar = false;
                    }
                }

            });


            if (submeter && nomeDoCampo != '') {
                this.modalAlerta = {
                    mostrar: true,
                    tipo: 'warning',
                    titulo: 'Atenção: Documento obrigatório',
                    mensagem: `O documento ${nomeDoCampo} é obrigatório e não foi enviado, envie o documento para avançar`
                };
            }

            return this.permitirAvancar;

        },
        proximaEtapa() {
            if (this.validarFormulario(true)) {
                // Salvar todos os arquivos acumulados antes de avançar
                if (this.arquivosUpload && this.arquivosUpload.length > 0) {
                    this.requisicoes.listaDocumentosUploadEfetuado = false;
                }
                this.$store.commit('SET_SALVAR_INFO_DES_NOTIFICACAO', true);
                this.$store.commit('SET_PJ_SALVAR_INFO', true);
                this.$store.commit('SET_ETAPA_CADASTRO', 7);
            }
            
        },
        triggerFileInput(index) {
            // Garantir que o elemento existe
            if (!this.$refs['fileInput' + index] || !this.$refs['fileInput' + index][0]) {
                return;
            }
            
            const fileInput = this.$refs['fileInput' + index][0];
            
            // Para iOS Safari, precisamos garantir que o input esteja visível durante o clique
            const originalDisplay = fileInput.style.display;
            fileInput.style.display = 'block';
            fileInput.style.position = 'absolute';
            fileInput.style.opacity = '0.01';
            fileInput.style.zIndex = '9999';
            
            // Usando um timeout pequeno para garantir que o DOM foi atualizado
            setTimeout(() => {
                // Simulando um toque/clique nativo para melhor compatibilidade com iOS
                const event = new MouseEvent('click', {
                    view: window,
                    bubbles: true,
                    cancelable: true
                });
                fileInput.dispatchEvent(event);
                
                // Restaurar a exibição original após outro pequeno delay
                setTimeout(() => {
                    fileInput.style.display = originalDisplay;
                }, 200);
            }, 50);
        },
        getDocumentoStyle() {
            return {
                color: 'white',
                backgroundColor: '#00235C',
                border: '1px solid #00235C'
            };
        },
        async excluirArquivo(arquivo, index) {
             
            this.deletando = index;

            await api.post(`/cliente/documentos/excluir`, arquivo)
                .then((retorno) => {
                    this.deletando = -1;

                    if (retorno.status == 200) {

                        this.$store.dispatch('FETCH_CONSULTA_LISTA_DOCUMENTOS_UPLOAD_REALIZADO');

                        this.$swal({
                            icon: 'success',
                            title: 'Exclusão realizada com sucesso!',
                            text: 'Arquivo informado foi excluído.',
                        });

                        this.validarFormulario();
                    }else{
                      this.$swal({
                        icon: 'error',
                        title: 'Erro',
                        text: `Falha ao realizar exclusão do arquivo.`,
                        });
                    }
                })
                .catch((error) => {
                    this.deletando = -1;
                    this.$swal({
                        icon: 'error',
                        title: 'Falha ao realizar exclusão do arquivo.',
                        text: `${error.response.data.data[0]}`,
                    });
                });

            this.validarFormulario();

        },
        salvarAlteracoesLocalmente() {
            // Apenas atualiza o store com os arquivos pendentes, sem salvar ainda
            this.$store.commit('SET_SALVAR_INFO_UPLOAD', this.arquivosUpload);
            this.validarFormulario();
            // Não disparar salvamento automático - os arquivos ficarão acumulados
            // até o usuário clicar em "Avançar"
        },
        removerArquivoPendente(index) {
            // Remove arquivo da fila de pendentes
            this.arquivosUpload.splice(index, 1);
            // Atualiza o store
            this.$store.commit('SET_SALVAR_INFO_UPLOAD', this.arquivosUpload);
            this.validarFormulario();
        },
        salvar() {
                this.$store.commit('SET_PJ_SALVAR_INFO', true);
        },
        handleFileChange(event, id) {
            const file = event.target.files[0];
            if (!file) {
                console.error('Nenhum arquivo selecionado');
                return;
            }
            
            // Salvando informações do arquivo para referência
            const fileName = file.name;
            // Verificar tipo e tamanho do arquivo antes de prosseguir
            // Opcional: adicione suas verificações de validação aqui
            
            const reader = new FileReader();
            
            // Manipuladores de eventos para capturar erros
            reader.onerror = (error) => {
                console.error('Erro ao ler o arquivo:', error);
                this.$swal({
                    icon: 'error',
                    title: 'Erro no upload',
                    text: 'Houve um problema ao processar o arquivo. Por favor, tente novamente.',
                });
            };
            
            reader.onabort = () => {
                console.warn('Leitura de arquivo cancelada');
            };
            
            reader.onload = (e) => {
                try {
                    const base64String = e.target.result;
                    // Verificar se o resultado é uma string válida
                    if (typeof base64String !== 'string') {
                        throw new Error('Resultado inválido da leitura do arquivo');
                    }
                    
                    // Remover o prefixo "data:*/*;base64," se presente
                    let base64Data = base64String;
                    if (base64String.includes(',')) {
                        base64Data = base64String.split(',')[1];
                    }
                    
                    // Criar objeto de arquivo com os dados necessários
                    this.novoArquivo = {
                        TipoDocumentoId: id,
                        FileName: fileName,
                        Descricao: '',
                        FileDataString64: base64Data,
                        UsuarioInsert: this.user.NomeRazaoSocial,
                        // Adicionar timestamp para evitar problemas de cache
                        timestamp: new Date().getTime()
                    };
                    
                    // Adicionar ao array de uploads e limpar seleção
                    this.arquivosUpload.push({ ...this.novoArquivo });
                    this.TipoDocumento = '';
                    this.salvarAlteracoesLocalmente();

                } catch (error) {
                    console.error('Erro ao processar arquivo:', error);
                    this.$swal({
                        icon: 'error',
                        title: 'Erro no processamento',
                        text: 'Não foi possível processar o arquivo selecionado.',
                    });
                }
            };
            
            // Usar setTimeout para evitar problemas de timing no Safari iOS
            setTimeout(() => {
                // Tentar ler como DataURL para garantir melhor compatibilidade com Safari
                reader.readAsDataURL(file);
            }, 100);
            
            // Limpar o input para permitir a seleção do mesmo arquivo novamente
            // Usar setTimeout para evitar problemas de timing no Safari
            setTimeout(() => {
                event.target.value = null;
            }, 200);
        }
    }
}

</script>


<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');
</style>